import { useState, useEffect } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { API, axios } from "@/api";
import {
  Gift, Clock, Car, Star, Loader2, PartyPopper, Route, Calendar,
  ExternalLink, MapPin,
} from "lucide-react";

const SLOT_COLORS = {
  morning: { text: "text-amber-700", label: "Morning" },
  lunch: { text: "text-orange-700", label: "Lunch" },
  afternoon: { text: "text-sky-700", label: "Afternoon" },
  evening: { text: "text-indigo-700", label: "Evening" },
};
const SLOT_TIMES = { morning: "8:00 AM", lunch: "12:00 PM", afternoon: "2:30 PM", evening: "6:00 PM" };

export default function SharedItineraryPage() {
  const { shareId } = useParams();
  const navigate = useNavigate();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  useEffect(() => {
    (async () => {
      try {
        const res = await axios.get(`${API}/itinerary/shared/${shareId}`);
        setData(res.data);
      } catch { setError("This itinerary link is no longer available."); }
      finally { setLoading(false); }
    })();
  }, [shareId]);

  if (loading) return <div className="min-h-screen bg-slate-50 flex items-center justify-center"><Loader2 className="w-8 h-8 animate-spin text-pink-400" /></div>;
  if (error) return (
    <div className="min-h-screen bg-slate-50 flex flex-col items-center justify-center px-6 text-center">
      <PartyPopper className="w-12 h-12 text-slate-300 mb-4" />
      <p className="text-lg font-bold text-slate-700">{error}</p>
      <button onClick={() => navigate("/")} className="mt-4 px-6 py-2.5 bg-pink-500 text-white text-sm font-bold rounded-xl" data-testid="shared-go-home">Sign up to plan yours!</button>
    </div>
  );

  const it = data.itinerary;
  const stops = it?.stops || [];
  const totalSavings = it?.total_savings || 0;

  return (
    <div className="min-h-screen bg-slate-50 pb-12" data-testid="shared-itinerary-page">
      {/* Header */}
      <div className="bg-gradient-to-r from-pink-500 to-rose-500 px-4 py-6 text-white text-center">
        <p className="text-[10px] font-bold text-pink-200 uppercase tracking-wider">{data.user_name}'s Birthday Plan</p>
        <p className="text-3xl font-black mt-1">${totalSavings} in Birthday Deals</p>
        <div className="flex justify-center gap-4 mt-2">
          <div><p className="text-2xl font-black">{stops.length}</p><p className="text-[10px] text-pink-200">stops</p></div>
          {it.route_optimized && <div><p className="text-2xl font-black">{it.total_distance_mi}</p><p className="text-[10px] text-pink-200">miles</p></div>}
          {it.route_optimized && <div><p className="text-2xl font-black">{it.total_travel_min}</p><p className="text-[10px] text-pink-200">min drive</p></div>}
        </div>
        {it.location && (
          <div className="flex items-center justify-center gap-1 mt-2 text-pink-200 text-xs"><MapPin className="w-3 h-3" /> {it.location.city}{it.location.state ? `, ${it.location.state}` : ""}</div>
        )}
      </div>

      {/* Stops */}
      <div className="px-4 pt-4 space-y-3">
        {Array.from(new Set(stops.map(s => s.day))).sort().map(day => (
          <div key={day}>
            {it.num_days > 1 && <p className="text-xs font-bold text-pink-500 uppercase tracking-wider mb-2 mt-3" data-testid={`shared-day-${day}`}>Day {day}</p>}
            {["morning", "lunch", "afternoon", "evening"].map(slot => {
              const slotStops = stops.filter(s => s.day === day && s.time_slot === slot);
              if (slotStops.length === 0) return null;
              const sc = SLOT_COLORS[slot];
              return (
                <div key={slot} className="mb-3">
                  <div className="flex items-center gap-2 mb-1.5">
                    <Clock className={`w-3 h-3 ${sc.text}`} />
                    <span className={`text-[10px] font-bold ${sc.text} uppercase tracking-wider`}>{sc.label}</span>
                    <span className="text-[9px] text-slate-400">{SLOT_TIMES[slot]}</span>
                  </div>
                  <div className="space-y-1.5 ml-5 border-l-2 border-dashed border-slate-200 pl-3">
                    {slotStops.map(stop => (
                      <div key={stop.id} className="bg-white border border-slate-100 rounded-xl p-3" data-testid={`shared-stop-${stop.id}`}>
                        <div className="flex items-center gap-3">
                          <div className="w-10 h-10 rounded-lg bg-slate-50 flex items-center justify-center overflow-hidden border border-slate-100">
                            {stop.logo_url ? <img src={stop.logo_url} alt="" className="w-full h-full object-contain p-0.5" onError={e => e.target.style.display = "none"} /> : <Gift className="w-5 h-5 text-slate-300" />}
                          </div>
                          <div className="flex-1 min-w-0">
                            <h3 className="text-sm font-bold text-slate-800 truncate">{stop.name}</h3>
                            <p className="text-[10px] text-slate-500 line-clamp-1">{stop.offer_title}</p>
                            <div className="flex items-center gap-2 mt-0.5">
                              <span className="text-[10px] font-bold text-emerald-600">${stop.value_est}</span>
                              <span className="text-[9px] text-slate-300">|</span>
                              <span className="text-[9px] text-slate-400 flex items-center gap-0.5"><Star className="w-2.5 h-2.5 text-amber-400 fill-amber-400" />{stop.ease_stars}</span>
                            </div>
                          </div>
                          {stop.lat && stop.lng && (
                            <a href={`https://www.google.com/maps/dir/?api=1&destination=${stop.lat},${stop.lng}`} target="_blank" rel="noopener noreferrer" className="p-1.5 rounded-lg bg-slate-50 text-slate-400 hover:bg-blue-50 hover:text-blue-500" data-testid={`shared-nav-${stop.id}`}>
                              <ExternalLink className="w-3.5 h-3.5" />
                            </a>
                          )}
                        </div>
                      </div>
                    ))}
                  </div>
                </div>
              );
            })}
          </div>
        ))}
      </div>

      {/* CTA */}
      <div className="px-4 pt-6 text-center">
        <button onClick={() => navigate("/")} className="px-8 py-3.5 bg-gradient-to-r from-pink-500 to-rose-500 text-white text-sm font-bold rounded-xl shadow-lg shadow-pink-200" data-testid="shared-signup-cta">
          <Calendar className="w-4 h-4 inline mr-2" />Plan Your Own Birthday Deals!
        </button>
        <p className="text-[9px] text-slate-400 mt-2">It's free — find ${totalSavings}+ in birthday deals near you</p>
      </div>
    </div>
  );
}
